import React, { Component } from 'react'

import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import get from 'lodash/get'

import PageTitle from '../../../components/ui/typography/PageTitle'
import { currentUserSuccess } from '../../../store/shared/currentUserDuck'
import selectors from '../../../store/shared/selectors'
import intercom from '../../../utils/intercom/intercomUtils'
import { performRetrieveUserProfile } from '../UserProfile/userProfileDuck'

import QuestionsWrapper from './components/QuestionsWrapper'
import ResultsWrapper from './components/ResultsWrapper'
import * as personalityAssessmentDuck from './personalityAssessmentDuck'
import LottieLoader from '../../../components/ui/lotties/LottieLoader'
import sendFullStoryEvent from '../../../utils/analyticsHelpers/sendFullStoryEvent'
import fullstory from '../../../utils/constants/fullstory'
import uberFacts from '../../../assets/usageTips/tips.json'

class AuthenticatedPersonalityAssessmentIndex extends Component {
  static propTypes = {
    result: PropTypes.any,
    activeQuestionIndex: PropTypes.number.isRequired,
    answers: PropTypes.array.isRequired,
    bmcode: PropTypes.string,
    currentUserSuccess: PropTypes.func.isRequired,
    gender: PropTypes.any.isRequired,
    getPersonalityTypeResults: PropTypes.func.isRequired,
    goToNextQuestion: PropTypes.func.isRequired,
    goToPreviousQuestion: PropTypes.func.isRequired,
    hasNextQuestion: PropTypes.bool.isRequired,
    hasPreviousQuestion: PropTypes.bool.isRequired,
    isRequestingQuestions: PropTypes.bool.isRequired,
    isSubmittingAnswers: PropTypes.bool.isRequired,
    loadingPersonalityResultData: PropTypes.bool.isRequired,
    performAnalyzePersonalityAssessmentAnswers: PropTypes.func.isRequired,
    performRetrievePersonalityAssessmentQuestions: PropTypes.func.isRequired,
    performRetrieveUserProfile: PropTypes.func.isRequired,
    personalityResultData: PropTypes.object,
    questions: PropTypes.array.isRequired,
    resetPersonalityAssessment: PropTypes.func.isRequired,
    userId: PropTypes.string,
  }

  constructor(props) {
    super(props)

    this.getPersonalityResultsForUser = () => {
      const bmcode = get(this.props, 'result.personalitySummary.bmcode')
      if (bmcode) {
        this.props.getPersonalityTypeResults(bmcode)
      }
    }

    this.compareBmCode = prevProps => {
      const bmcode = get(this.props, 'result.personalitySummary.bmcode')
      const prevBmcode = get(prevProps, 'result.personalitySummary.bmcode')

      if (bmcode !== prevBmcode) {
        this.getPersonalityResultsForUser()
      }
    }

    this.updateUserBmCode = bmcode => {
      intercom.intercomUpdate({
        user_id: this.props.userId,
        personality: bmcode,
      })
    }

    this.saveAnswer = answer => {
      const answerString = `${this.props.activeQuestionIndex + 1}${answer}`
      if (this.props.hasNextQuestion) {
        this.props.goToNextQuestion(answerString)
      } else {
        this.props
          .performAnalyzePersonalityAssessmentAnswers(this.props.answers.concat(answerString))
          .then(data => {
            setTimeout(() => {
              this.props.performRetrieveUserProfile().then(this.props.currentUserSuccess)
              const bmcode = get(data, 'result.personalitySummary.bmcode')
              this.updateUserBmCode(bmcode)

              const eventProperties = {
                personality_type_str: bmcode,
              }
              sendFullStoryEvent(
                fullstory.eventNames.viewPersonalityAssessmentResults,
                eventProperties,
              )
            }, 300)
          })
      }
    }

    this.goBack = () => this.props.hasPreviousQuestion && this.props.goToPreviousQuestion()

    this.renderQuestions = () => {
      const { activeQuestionIndex, hasPreviousQuestion, questions } = this.props
      return (
        <div>
          <PageTitle delay={0.2} content="Tell Us About Yourself" tourName="personalityQuestions" />
          <QuestionsWrapper
            activeQuestionIndex={activeQuestionIndex}
            questions={questions}
            hasPreviousQuestion={hasPreviousQuestion}
            goBack={this.goBack}
            saveAnswer={this.saveAnswer}
          />
        </div>
      )
    }

    this.renderResult = () => {
      const {
        resetPersonalityAssessment,
        result,
        personalityResultData,
        gender,
        loadingPersonalityResultData,
      } = this.props
      return (
        <LottieLoader
          animationName="GEARS"
          isReady={!loadingPersonalityResultData}
          loadingTime={2500}
          uberFacts={uberFacts}
          noMessage
          height={500}
          width={300}
        >
          <div>
            <PageTitle delay={0.3} content="Your Assessment Results" tourName="personalityResult" />
            <ResultsWrapper
              result={result}
              personalityResultData={personalityResultData}
              clear={resetPersonalityAssessment}
              gender={gender}
            />
          </div>
        </LottieLoader>
      )
    }
  }

  componentWillMount() {
    this.props.performRetrievePersonalityAssessmentQuestions()
  }

  componentDidMount() {
    const { personalityResultData } = this.props

    if (!personalityResultData) {
      this.getPersonalityResultsForUser()
    }

    intercom.intercomUpdate({
      user_id: this.props.userId,
    })
  }

  componentDidUpdate(prevProps) {
    this.compareBmCode(prevProps)
  }

  render() {
    const { result, loadingPersonalityResultData } = this.props

    return (
      <div>
        <div className="main-page">
          {result || loadingPersonalityResultData ? this.renderResult() : this.renderQuestions()}
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  activeQuestionIndex: state.personalityAssessment.activeQuestionIndex,
  isRequestingQuestions:
    state.personalityAssessment.retrieveQuestions.requestStarted &&
    !state.personalityAssessment.retrieveQuestions.requestEnded,
  isSubmittingAnswers:
    state.personalityAssessment.submitAnswers.requestStarted &&
    !state.personalityAssessment.submitAnswers.requestEnded,
  questions: selectors.getPersonalityAssessmentQuestions(state.personalityAssessment),
  answers: selectors.getPersonalityAssessmentAnswers(state.personalityAssessment),
  hasNextQuestion: selectors.getHasNextQuestion(state.personalityAssessment),
  hasPreviousQuestion: selectors.getHasPreviousQuestion(state.personalityAssessment),
  personalityResultsFromCMS: state.personalityAssessment.personalityResultsFromCMS,
  result: state.personalityAssessment.result,
  gender: selectors.getGender(state),
  userId: selectors.getUserId(state),
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      performRetrievePersonalityAssessmentQuestions:
        personalityAssessmentDuck.performRetrievePersonalityAssessmentQuestions,
      performAnalyzePersonalityAssessmentAnswers:
        personalityAssessmentDuck.performAnalyzePersonalityAssessmentAnswers,
      goToNextQuestion: personalityAssessmentDuck.goToNextQuestion,
      goToPreviousQuestion: personalityAssessmentDuck.goToPreviousQuestion,
      resetPersonalityAssessment: personalityAssessmentDuck.resetPersonalityAssessment,
      currentUserSuccess,
      performRetrieveUserProfile,
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(AuthenticatedPersonalityAssessmentIndex)



// WEBPACK FOOTER //
// ./src/routes/authenticated/PersonalityAssessment/AuthenticatedPersonalityAssessmentIndex.js